/// <reference types="google.maps" preserve="true" />

import { Loader } from '@googlemaps/js-api-loader';

import {
  GeolocationUnsupportedError,
  GoogleMapsAPINotFoundError,
  IsNotAnObjectError,
  ProviderNotSupportedError
} from './Location.types';
import type { LatLng, MapsProvider, ProviderOpts } from './Location.types';
import type { LocationAPI } from './LocationAPI.types';
import GoogleMapsAPI from './GoogleMapsAPI';

const DegreeRegexChunks = [
  /^[-+]?/,
  // latitude degrees
  /([0-9]|[1-8][0-9]|90)(\.\d+)?°/,
  /\s?/,
  // latitude direction
  /(N|S)?/,
  // sepeartor between latitude and longitude
  /(,|,\s|\s)/,
  /[-+]?/,
  // longitude degrees
  /([0-9]|[1-9][0-9]|1[0-7][0-9]|180)(\.\d+)?°/,
  /\s?/,
  // longitude direction
  /(W|E)?$/
];

const DegreeRegularExpression = new RegExp(DegreeRegexChunks.map(c => c.source).join(''));

const DMRegexChunks = [
  /^[-+]?/,
  // latitude degrees
  /([0-9]|[1-8][0-9]|90)°/,
  /\s?/,
  // latitude minutes
  /([0-9]|[0-5][0-9])(\.\d+)?('|’)/,
  /\s?/,
  // latitude direction
  /(N|S)?/,
  // sepeartor between latitude and longitude
  /(,|,\s|\s)/,
  /[-+]?/,
  // longitude degrees
  /([0-9]|[1-9][0-9]|1[0-7][0-9]|180)°/,
  /\s?/,
  // longitude minutes
  /([0-9]|[0-5][0-9])(\.\d+)?('|’)/,
  /\s?/,
  // longitude direction
  /(W|E)?$/
];

const DMRegularExpression = new RegExp(DMRegexChunks.map(c => c.source).join(''));
const DMSRegexChunks = [
  /^[-+]?/,
  // latitude degrees
  /([0-9]|[1-8][0-9]|90)°/,
  /\s?/,
  // latitude minutes
  /([0-9]|[0-5][0-9])('|’)/,
  /\s?/,
  // latitude seconds
  /([0-9]|[0-5][0-9])(\.\d+)?("|″)/,
  /\s?/,
  // latitude direction
  /(N|S)?/,
  // sepeartor between latitude and longitude
  /(,|,\s|\s)/,
  /[-+]?/,
  // longitude degrees
  /([0-9]|[1-9][0-9]|1[0-7][0-9]|180)°/,
  /\s?/,
  // longitude minutes
  /([0-9]|[0-5][0-9])('|’)/,
  /\s?/,
  // longitude seconds
  /([0-9]|[0-5][0-9])(\.\d+)?("|″)/,
  /\s?/,
  // longitude direction
  /(W|E)?$/
];

const DMSRegularExpression = new RegExp(DMSRegexChunks.map(c => c.source).join(''));

const decimalRegexChunks = [
  /^[-+]?/,
  /([0-9]|[1-8][0-9]|90)/,
  /(\.\d+)?/,
  /(,|\s|,\s)/,
  /[-+]?/,
  /([0-9]|[1-9][0-9]|1[0-7][0-9]|180)/,
  /(\.\d+)?$/
];

const decimalFormatCoordinatesRegularExpression = new RegExp(
  decimalRegexChunks.map(c => c.source).join('')
);

const providers: {
  [key: string]: MapsProvider;
} = {
  google: {
    name: 'Google',
    loadedPromise: null,
    loadedApiKey: '',
    loadAPI(opts) {
      const loader = new Loader({
        version: 'quarterly',
        apiKey: opts.apiKey ?? '',
        region: opts.region,
        language: opts.language,
        libraries: ['places', 'marker']
      });

      return loader.load().then(
        () => new GoogleMapsAPI(),
        () => {
          throw new Error(GoogleMapsAPINotFoundError);
        }
      );
    }
  },
  undefined: {
    name: 'undefined',
    loadedPromise: null,
    loadedApiKey: '',
    loadAPI: data => Promise.reject(new Error(`${ProviderNotSupportedError}: ${data.name}`))
  }
};

export async function loadMapsAPI(name: string = '', opts?: ProviderOpts): Promise<LocationAPI> {
  if (typeof opts !== 'object' || !opts) {
    throw new Error(IsNotAnObjectError);
  }

  const provider = providers[name] ?? providers.undefined;

  if (!provider.loadedPromise) {
    provider.loadedPromise = provider
      .loadAPI(opts)
      .then(ApiObj => {
        provider.loadedApiKey = opts?.apiKey ?? '';
        return ApiObj;
      })
      .catch(err => {
        provider.loadedPromise = null;
        throw new Error(err);
      });
  }
  return provider.loadedPromise;
}

/**
 * Identifies coordinates in degree format
 * eg. 45.78°N 9.11°E
 */
export function isValueDegreeFormatCoordinate(value: string): boolean {
  return DegreeRegularExpression.test(value);
}

/**
 * Identifies coordinates in degree, minute format
 * eg. 45°45.4’N 9°23’E
 */
export function isValueDMCoordinate(value: string): boolean {
  return DMRegularExpression.test(value);
}

/**
 * Identifies coordinates in degree, minute, second format
 * eg. 45°45’32.4″N 9°23’39.9″E
 */
export function isValueDMSCoordinate(value: string): boolean {
  return DMSRegularExpression.test(value);
}

/**
 * Identifies coordinates in decimal format
 * eg. 14.678, -38.765
 */
export function isValueACoordinate(value: string): boolean {
  return (
    decimalFormatCoordinatesRegularExpression.test(value) ||
    isValueDMSCoordinate(value) ||
    isValueDMCoordinate(value) ||
    isValueDegreeFormatCoordinate(value)
  );
}

interface DMSParts {
  degrees: number;
  minutes?: number;
  seconds?: number;
}

/**
 * Converts DMS (degree, minute, second) format latitude/longitude into decimal format.
 */
function convertDMSToDD({ degrees, minutes, seconds }: DMSParts, direction?: string): number {
  let dd = degrees;
  if (minutes) {
    if (degrees > 0) dd += minutes / 60;
    if (degrees < 0) dd -= minutes / 60;
  }
  if (seconds) {
    if (degrees > 0) dd += seconds / (60 * 60);
    if (degrees < 0) dd -= seconds / (60 * 60);
  }

  if (degrees > 0 && (direction === 'S' || direction === 'W')) {
    dd *= -1;
  }

  return dd;
}

/**
 * Parses DMS (degree, minute, second) format string into decimal format coordinates.
 */
export function parseDMS(input: string): number[] {
  if (isValueDMSCoordinate(input)) {
    const parts = input.split(/'|’|"\s?|″\s?|°|,?\s|,\s?/);

    const lat = convertDMSToDD(
      {
        degrees: Number(parts[0]),
        minutes: Number(parts[1]),
        seconds: Number(parts[2])
      },
      parts[3]
    );
    const lng = convertDMSToDD(
      {
        degrees: Number(parts[4]),
        minutes: Number(parts[5]),
        seconds: Number(parts[6])
      },
      parts[7]
    );
    return [lat, lng];
  }
  if (isValueDMCoordinate(input)) {
    const parts = input.split(/'\s?|’\s?|°|,?\s|,\s?/);

    const lat = convertDMSToDD({ degrees: Number(parts[0]), minutes: Number(parts[1]) }, parts[2]);
    const lng = convertDMSToDD({ degrees: Number(parts[3]), minutes: Number(parts[4]) }, parts[5]);
    return [lat, lng];
  }

  const parts = input.split(/°\s?|,?\s|,\s?/);

  const lat = convertDMSToDD({ degrees: Number(parts[0]) }, parts[1]);
  const lng = convertDMSToDD({ degrees: Number(parts[2]) }, parts[3]);
  return [lat, lng];
}

export async function getNavigatorPosition(): Promise<LatLng> {
  if (!navigator.geolocation) throw new Error(GeolocationUnsupportedError);

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      currentLocation => {
        resolve({
          latitude: currentLocation.coords.latitude,
          longitude: currentLocation.coords.longitude
        });
      },
      error => reject(new Error(error.message))
    );
  });
}

export function toGoogleLatLng(coords: LatLng): google.maps.LatLng {
  return new google.maps.LatLng(coords.latitude, coords.longitude);
}

export const isLatLngObject = (obj: unknown): obj is google.maps.LatLng => {
  return (
    !!obj &&
    typeof obj === 'object' &&
    'lat' in obj &&
    typeof obj.lat === 'function' &&
    'lng' in obj &&
    typeof obj.lng === 'function'
  );
};

export const getGoogleMapsDirHref = (
  destination: string,
  waypoints: string[],
  travelMode: string = 'driving'
) => {
  const url = new URL('https://www.google.com/maps/dir/?api=1');

  url.searchParams.append('destination', destination);
  url.searchParams.append('waypoints', waypoints.join('|'));
  url.searchParams.append('travelmode', travelMode);

  return url.href;
};
